import { ColumnDef } from "@tanstack/react-table";
import { Avatar, Button } from "flowbite-react";
import { Eye, UserPlus } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AnimatedModal from "../../../components/AnimatedModal";
import SearchInput from "../../../components/SearchInput";
import { Table } from "../../../components/Table";
import { useMerchants } from "../../../hooks/get";
import { IMerchant } from "../../../interfaces";
import TeamMembersModal from "../components/TeamMembersListComponent";
import {
  DEFAULT_AVATAR,
  DEFAULT_ITEMS_PER_PAGE,
} from "../../../shared/constants";

const MerchantsContainer = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const [showTeamMembersModal, setShowTeamMembersModal] = useState(false);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<IMerchant[]>(
    []
  );

  const { data, isLoading } = useMerchants();

  // Search handler
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Filtered merchants
  const filteredMerchants = useMemo(() => {
    // Only show admins
    const admins = [...(data?.results || [])]
      .filter((merchant) => merchant.is_team_admin)
      .map((admin) => ({
        ...admin,
        team_members: [...(data?.results || [])].filter(
          (dt) => dt.role === "sub_admin" && dt.schema === admin.schema
        ),
      }));
    if (!searchTerm) return admins;
    return admins.filter((merchant) => {
      const search = searchTerm.toLowerCase();
      return (
        merchant.name?.toLowerCase().includes(search) ||
        merchant.schema?.toLowerCase().includes(search) ||
        merchant.company_name?.toLowerCase().includes(search) ||
        merchant.email.toLowerCase().includes(search)
      );
    });
  }, [data, searchTerm]);

  // Table columns
  const columns: ColumnDef<IMerchant>[] = [
    {
      accessorKey: "schema",
      header: t("merchants.table.name"),
      size: 200,
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            <Avatar
              img={(row.original?.profile_picture as string) || DEFAULT_AVATAR}
              rounded
              size="xs"
              alt={
                row.original.name || row.original.schema || row.original.email
              }
            />
            {row.original.name || "-"}
          </div>
        );
      },
    },
    {
      accessorKey: "email",
      size: 250,
      header: t("merchants.table.email"),
    },
    {
      accessorKey: "company_name",
      header: t("merchants.table.company"),
      cell: ({ row }) => row.original.company_name || "-",
    },
    {
      accessorKey: "is_active",
      header: t("merchants.table.status"),
      cell: ({ row }) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.original.is_active
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {row.original.is_active
            ? t("merchants.status.active")
            : t("merchants.status.inactive")}
        </span>
      ),
    },
    {
      accessorKey: "created_at",
      header: t("common.createdAt"),
      cell: ({ row }) =>
        row.original.created_at
          ? new Date(row.original.created_at).toLocaleDateString()
          : "-",
    },
    {
      accessorKey: "updated_at",
      header: t("common.updatedAt"),
      cell: ({ row }) =>
        row.original.updated_at
          ? new Date(row.original.updated_at).toLocaleDateString()
          : "-",
    },
    {
      id: "actions",
      header: t("merchants.table.actions"),
      cell: ({ row }) => (
        <div
          className="flex items-center gap-2 min-w-[100px]"
          style={{ width: 100 }}
        >
          {row.original.team_members.length > 0 && (
            <Button
              title={t("merchants.actions.view_team_members")}
              size="xs"
              color="light"
              onClick={() => {
                setSelectedTeamMembers(row.original.team_members || []);
                setShowTeamMembersModal(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
      size: 100,
    },
  ];

  // Responsive layout and improved table
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-2 mb-4">
        <div className="flex items-center gap-2">
          <UserPlus size={20} className="text-gray-700" />
          <h1 className="text-md sm:text-xl font-bold text-gray-700">
            {t("merchants.title")}
          </h1>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <SearchInput
            onSearch={handleSearch}
            placeholder={t("common.search")}
            className="w-full sm:w-[250px]"
            style={{ height: "38px" }}
            size="md"
          />
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg mt-2">
        <Table
          data={filteredMerchants}
          columns={columns}
          isLoading={isLoading}
          showPagination={true}
          showSearch={false}
          loadingHeight={3.97}
          loadingRowCount={12}
          pageSize={DEFAULT_ITEMS_PER_PAGE}
          maxHeight={`calc(100vh - ${isLoading ? 148 : 209}px)`}
        />
      </div>
      {/* Team Members Modal */}
      <AnimatedModal
        show={showTeamMembersModal}
        onClose={() => setShowTeamMembersModal(false)}
        title={t("merchant.teamMembers")}
        children={<TeamMembersModal teamMembers={selectedTeamMembers} />}
        size="xl"
      />
    </div>
  );
};

export default MerchantsContainer;
